/**
 * Field Type Resolvers (Portal)
 *
 * Pure synchronous functions over the loaded registry. Resolution is
 * case-insensitive on string inputs and falls back to "Text" for unknown
 * inputs so render code never crashes on a typo or a future field type
 * the bundled snapshot doesn't know about.
 */

import { getRegistry } from './fieldRegistry';

let _aliasIndex = null;
let _codeIndex = null;
let _presentationIndex = null;

function aliasIndex() {
  const { payload } = getRegistry();
  if (_aliasIndex && _aliasIndex.version === payload.version) return _aliasIndex.map;
  const map = new Map();
  for (const entry of payload.entries) {
    map.set(entry.key.toLowerCase(), entry);
    for (const alias of entry.aliases) map.set(alias.toLowerCase(), entry);
  }
  _aliasIndex = { version: payload.version, map };
  return map;
}

function codeIndex() {
  const { payload } = getRegistry();
  if (_codeIndex && _codeIndex.version === payload.version) return _codeIndex.map;
  const map = new Map();
  for (const entry of payload.entries) {
    map.set(entry.code, entry);
    for (const dep of entry.deprecatedCodes) map.set(dep, entry);
  }
  _codeIndex = { version: payload.version, map };
  return map;
}

function presentationIndex() {
  const { payload } = getRegistry();
  if (_presentationIndex && _presentationIndex.version === payload.version)
    return _presentationIndex.map;
  const map = new Map();
  for (const entry of payload.presentation) map.set(entry.key, entry);
  _presentationIndex = { version: payload.version, map };
  return map;
}

export function getEntry(input) {
  if (typeof input === 'number') {
    const found = codeIndex().get(input);
    if (found) return found;
  } else if (typeof input === 'string') {
    const found = aliasIndex().get(input.toLowerCase());
    if (found) return found;
  }
  return aliasIndex().get('text');
}

/** Canonical PascalCase key (e.g. "Radio", "QRCode"). */
export function resolveType(input) {
  return getEntry(input).key;
}

export function getTypeCode(input) {
  return getEntry(input).code;
}

export function getFlags(input) {
  return getEntry(input).flags;
}

export function isLayoutType(input) {
  return getEntry(input).flags.isLayout;
}

export function isMediaType(input) {
  return getEntry(input).flags.isMedia;
}

export function hasOptions(input) {
  return getEntry(input).flags.hasOptions;
}

export function getCategory(input) {
  return getEntry(input).category;
}

export function getIcon(input) {
  return getEntry(input).icon;
}

export function getValidation(input) {
  return getEntry(input).validation;
}

export function getValidationCapabilities(input) {
  return getEntry(input).validation.capabilities;
}

export function getDefaults(input) {
  return getEntry(input).defaults;
}

export function getAllTypes() {
  return getRegistry().payload.entries.slice();
}

export function getTypesByCategory(category) {
  return getRegistry().payload.entries.filter((e) => e.category === category);
}

export function getRendering(input, context) {
  const key = resolveType(input);
  return presentationIndex().get(key)?.rendering?.[context] ?? { component: 'text', props: {} };
}

export function getDisplay(input) {
  const key = resolveType(input);
  return presentationIndex().get(key)?.display;
}
